import React, { useState } from 'react';
import { useMPINVerification, MPINVerificationModal } from '../utils/mpinAccessUtils';

// Higher-Order Component to add MPIN protection to any component
export const withMPINProtection = (WrappedComponent, pageName, tabName = null) => {
  return function MPINProtectedComponent(props) {
    const [mpinVerified, setMpinVerified] = useState(false);
    
    const {
      requiresMPIN,
      hasMPIN,
      loading,
      mpinInput,
      setMpinInput,
      verificationError,
      isVerifying,
      handleMPINSubmit,
      closeMPINModal
    } = useMPINVerification(pageName, tabName);

    // If loading, show loading state
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    // If MPIN is required but user doesn't have MPIN set, show message
    if (requiresMPIN && !hasMPIN) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
              <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-yellow-900 mb-2">MPIN Required</h3>
            <p className="text-sm text-yellow-800 mb-4">
              This section requires MPIN verification, but you haven't set an MPIN yet.
            </p>
            <p className="text-sm text-yellow-700">
              Please go to <strong>Settings → Security & MPIN</strong> to set up your MPIN first.
            </p>
          </div>
        </div>
      );
    }

    // If MPIN is not required, render the component normally
    if (!requiresMPIN) {
      return <WrappedComponent {...props} />;
    }

    // If MPIN is required and user has MPIN, show MPIN verification modal
    // Don't render the component until MPIN is verified
    if (requiresMPIN && hasMPIN && !mpinVerified) {
      return (
        <div>
          <MPINVerificationModal
            isOpen={true} // Always show modal when MPIN is required
            onClose={closeMPINModal}
            onSubmit={async (e) => {
              const success = await handleMPINSubmit(e);
              if (success) {
                setMpinVerified(true);
              }
            }}
            mpinInput={mpinInput}
            setMpinInput={setMpinInput}
            verificationError={verificationError}
            isVerifying={isVerifying}
            title={`MPIN Required for ${pageName}${tabName ? ` - ${tabName}` : ''}`}
            message="Please enter your MPIN to access this section."
          />
        </div>
      );
    }

    // If MPIN is verified, render the component
    if (mpinVerified) {
      return <WrappedComponent {...props} />;
    }

    // Fallback - should not reach here
    return null;
  };
};

// Hook to use MPIN protection in functional components
export const useMPINProtection = (pageName, tabName = null) => {
  const {
    requiresMPIN,
    hasMPIN,
    loading,
    showMPINModal,
    mpinInput,
    setMpinInput,
    verificationError,
    isVerifying,
    handleMPINSubmit,
    openMPINModal,
    closeMPINModal
  } = useMPINVerification(pageName, tabName);

  const checkAccess = () => {
    if (requiresMPIN && hasMPIN) {
      openMPINModal();
      return false; // Access denied, waiting for MPIN
    }
    return true; // Access granted
  };

  return {
    requiresMPIN,
    hasMPIN,
    loading,
    showMPINModal,
    mpinInput,
    setMpinInput,
    verificationError,
    isVerifying,
    handleMPINSubmit,
    closeMPINModal,
    checkAccess
  };
};

// Component to show MPIN required message
export const MPINRequiredMessage = ({ pageName, tabName = null }) => (
  <div className="max-w-2xl mx-auto px-4 py-8">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
        <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-blue-900 mb-2">MPIN Verification Required</h3>
      <p className="text-sm text-blue-800 mb-4">
        This section requires MPIN verification for security.
      </p>
      <p className="text-sm text-blue-700">
        <strong>{pageName}{tabName ? ` - ${tabName}` : ''}</strong> contains sensitive business data.
      </p>
    </div>
  </div>
);

// Component to show MPIN not set message
export const MPINNotSetMessage = () => (
  <div className="max-w-2xl mx-auto px-4 py-8">
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
        <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-yellow-900 mb-2">MPIN Not Set</h3>
      <p className="text-sm text-yellow-800 mb-4">
        You need to set up an MPIN to access protected sections.
      </p>
      <p className="text-sm text-yellow-700">
        Please go to <strong>Settings → Security & MPIN</strong> to set up your MPIN.
      </p>
    </div>
  </div>
);
